import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Field, FieldConfig } from '@app/core/model/other/field-config';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { AbstractFieldGroupBuilder, FieldStateMode } from '@app/shared/components/fields/abstract.field';
import { SingleAssetMapComponent } from '@app/shared/components/map/single-asset-map.component';
import { getLocale } from '@app/shared/extra/utils';
import { TranslateService } from '@ngx-translate/core';
import structuredClone from '@ungap/structured-clone';
import PostalAddress from 'i18n-postal-address';
import { FormatTypes } from 'i18n-postal-address/dist/types/address-format';
import { LatLng } from 'leaflet';
import { BehaviorSubject, merge, ReplaySubject } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';

@Component({
  selector: 'address-field-builder',
  templateUrl: './address-field-builder.component.html',
  styleUrls: ['./address-field-builder.component.scss'],
  standalone: false
})
export class AddressFieldBuilderComponent extends AbstractFieldGroupBuilder implements OnInit, AfterViewInit {
  private translate = inject(TranslateService);
  public Permission = PermissionEnum;

  public addressOutput = new PostalAddress();
  public form: FormGroup<{
    streetNumber: FormControl<string>,
    street: FormControl<string>,
    postalCode: FormControl<string>,
    city: FormControl<string>,
    country: FormControl<string>,
    latitude: FormControl<string>,
    longitude: FormControl<string>
  }>;

  public completeAddress: {
    streetNumber?: string,
    street?: string,
    postalCode?: string,
    city?: string,
    country?: string,
    latitude?: string,
    longitude?: string
  };

  public streetNumberFieldConfig: FieldConfig;
  public streetFieldConfig: FieldConfig;
  public postalCodeFieldConfig: FieldConfig;
  public cityFieldConfig: FieldConfig;
  public countryFieldConfig: FieldConfig;
  public latitudeFieldConfig: FieldConfig;
  public longitudeFieldConfig: FieldConfig;

  @ViewChild(SingleAssetMapComponent, {static: false}) public map: SingleAssetMapComponent;

  public ngOnInit(): void {
    // Cannot edit this field's group if the user has no write access to some fields in this group
    this.preconditionsForEdition &&= this.fieldGroup.children.every(fieldConfig =>
      this.accessManager.hasFieldWriteAccess(fieldConfig.field.alias, fieldConfig.fieldEntityType)
    );

    this.streetNumberFieldConfig = this.fieldGroup.children.firstOrNull(fieldConfig => fieldConfig.code === 'streetNumber');
    this.streetFieldConfig = this.fieldGroup.children.firstOrNull(fieldConfig => fieldConfig.code === 'street');
    this.postalCodeFieldConfig = this.fieldGroup.children.firstOrNull(fieldConfig => fieldConfig.code === 'postalCode');
    this.cityFieldConfig = this.fieldGroup.children.firstOrNull(fieldConfig => fieldConfig.code === 'city');
    this.countryFieldConfig = this.fieldGroup.children.firstOrNull(fieldConfig => fieldConfig.code === 'country');
    this.latitudeFieldConfig = this.fieldGroup.children.firstOrNull(fieldConfig => fieldConfig.code === 'latitude');
    this.longitudeFieldConfig = this.fieldGroup.children.firstOrNull(fieldConfig => fieldConfig.code === 'longitude');

    this.setFieldGroupInfo([], {
      state: new ReplaySubject<FieldStateMode>(1),
      isSingleField: true,
      value: {},
      isDisplayed: new BehaviorSubject<boolean>(false),
      initialValue: {}
    });
    this.initForm();
    this.setAddressFormat();
  }

  public initForm(): void {
    this.completeAddress = {
      streetNumber: this.entity.properties['streetNumber'],
      street: this.entity.properties['street'],
      postalCode: this.entity.properties['postalCode'],
      city: this.entity.properties['city'],
      country: this.entity.properties['country'],
      latitude: this.entity.properties['latitude'],
      longitude: this.entity.properties['longitude']
    };

    this.form = new FormGroup({
      streetNumber: new FormControl(
        this.getFieldInitialValue(this.streetNumberFieldConfig?.field),
        this.getFieldValidators(this.streetNumberFieldConfig?.field)
      ),
      street: new FormControl(
        this.getFieldInitialValue(this.streetFieldConfig?.field),
        this.getFieldValidators(this.streetFieldConfig?.field)
      ),
      postalCode: new FormControl(
        this.getFieldInitialValue(this.postalCodeFieldConfig?.field),
        this.getFieldValidators(this.postalCodeFieldConfig?.field)
      ),
      city: new FormControl(
        this.getFieldInitialValue(this.cityFieldConfig?.field),
        this.getFieldValidators(this.cityFieldConfig?.field)
      ),
      country: new FormControl(
        this.getFieldInitialValue(this.countryFieldConfig?.field),
        this.getFieldValidators(this.countryFieldConfig?.field)
      ),
      latitude: new FormControl(
        this.getFieldInitialValue(this.latitudeFieldConfig?.field),
        this.getFieldValidators(this.latitudeFieldConfig?.field)
      ),
      longitude: new FormControl(
        this.getFieldInitialValue(this.longitudeFieldConfig?.field),
        this.getFieldValidators(this.longitudeFieldConfig?.field)
      )
    });

    // Perform a deep copy of the address
    this.setFieldGroupInitialValue(structuredClone(this.completeAddress));
    this.setFieldGroupValue(structuredClone(this.completeAddress));
    this.getNextState();

    merge(
      this.form.controls.streetNumber.valueChanges,
      this.form.controls.street.valueChanges,
      this.form.controls.city.valueChanges,
      this.form.controls.postalCode.valueChanges,
      this.form.controls.country.valueChanges
    )
      .pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.completeAddress = this.form.value;
      });
  }

  public ngAfterViewInit(): void {
    this.setupHooks();

    this.getFieldGroupState().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newMode) => {
      if (newMode === FieldStateMode.AFTER_SAVE) {
        this.refreshEntity();

        this.completeAddress = {
          streetNumber: this.entity.properties['streetNumber'],
          street: this.entity.properties['street'],
          postalCode: this.entity.properties['postalCode'],
          city: this.entity.properties['city'],
          country: this.entity.properties['country'],
          latitude: this.entity.properties['latitude'],
          longitude: this.entity.properties['longitude']
        };

        // Perform a deep copy of the address
        this.setFieldGroupInitialValue(structuredClone(this.completeAddress));
        this.setFieldGroupValue(structuredClone(this.completeAddress));

        this.getNextState();
      } else {
        this.currentMode = newMode;
      }
    });
  }

  /**
   * Retrieves a list of validator functions for a given field.
   *
   * @param field The field object containing validator information.
   * @return An array of validator functions associated with the field.
   */
  private getFieldValidators(field: Field): ValidatorFn[] {
    return field?.validators?.map(v => this.validationService.getValidator(v, this.entity)) ?? [];
  }

  /**
   * Retrieves the initial value of a specified field.
   *
   * @param field The field object containing validator information.
   * @return The initial value of the field. Returns an empty string if the field is not found.
   */
  private getFieldInitialValue(field: Field): string {
    if (!field?.code) return '';

    const initialValue = this.entity.properties[field.code] ?? '';
    if (['latitude', 'longitude'].includes(field.code)) {
      return initialValue.toString().replace('.', ',');
    }
    return initialValue;
  }

  /**
   * Updates the Lat/Lng fields in the form from the event data fired by the map component
   * @param {LatLng} event
   */
  public updateLatLong(event: LatLng): void {
    this.form.controls.latitude.setValue(this.formatCoordinateDisplay(event?.lat));
    this.form.controls.longitude.setValue(this.formatCoordinateDisplay(event?.lng));
  }

  /**
   * Prevents typing unwanted characters in a numeric field
   * @param {InputEvent} event
   * @returns {boolean}
   */
  public validateKeypressEvent(event: InputEvent): boolean {
    const input = event.data;
    const pattern = /[0-9\-.,]/;
    return event.inputType != 'insertText' || this.validateInput(input, pattern);
  }

  /**
   * Prevents pasting unwanted characters in a numeric field
   * @param {ClipboardEvent} event
   * @returns {boolean}
   */
  public validatePasteEvent(event: ClipboardEvent): boolean {
    const input = event.clipboardData.getData('text');
    const pattern = /^(?:-?\d+|-?\d{1,3}(?:\s?\d{3})+)?(?:[,.]\d+)?$/;
    return this.validateInput(input, pattern);
  }

  public save(): void {
    if (this.form.controls.latitude.value == '' || this.form.controls.longitude.value == '') {
      // Calculate coordinates automatically
      this.map.updateAddressCoordinates();
      this.map.positionUpdated.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.performSave();
      });
    } else {
      this.performSave();
    }
  }

  public performSave(): void {
    this.setFieldGroupValue({
      streetNumber: this.form.controls.streetNumber.value,
      street: this.form.controls.street.value,
      postalCode: this.form.controls.postalCode.value,
      city: this.form.controls.city.value,
      country: this.form.controls.country.value,
      latitude: this.formatCoordinateSave(this.form.controls.latitude.value),
      longitude: this.formatCoordinateSave(this.form.controls.longitude.value)
    });
    this.setAddressFormat();
    super.save();
  }

  public cancel(): void {
    this.initForm();
  }

  // TODO TTT-4135 Remove, iterate over errors instead of validators
  /**
   * List of validators for a certain address field for which an error has been raised by the corresponding ValidatorFn.
   * @param field Field's code.
   * @return List of FieldValidators.
   */
  public getErroredValidators(field: string): FieldValidator[] {
    const fieldConfig = this.fieldGroup.children.find(fieldConfig => fieldConfig.code === field);
    return fieldConfig?.field?.validators?.filter(validator => !!this.form.get(field).errors?.[validator.code])
      ?? [];
  }

  /**
   * Updates the address format according to the country.
   * @private
   */
  private setAddressFormat(): void {
    const locale = getLocale().substring(0, 2);
    this.addressOutput.setFormat({
      country: this.form.controls.country.value || this.appConfig.DEFAULT_ADDRESS_FORMAT,
      type: <FormatTypes>{'en': 'english', 'fr': 'french'}[locale] || 'default',
      useTransforms: false
    });
  }

  /**
   * Turns the address object into a humanized format to display
   * @returns {string}
   */
  public formatAddress(): string {
    if (this.form.controls.streetNumber.value) {
      this.addressOutput.setAddress(this.form.controls.streetNumber.value + ', ' + this.form.controls.street.value);
    } else {
      this.addressOutput.setAddress(this.form.controls.street.value);
    }
    this.addressOutput.setCity(this.form.controls.city.value);
    this.addressOutput.setPostalCode(this.form.controls.postalCode.value);

    if (this.form.controls.country.value) {
      this.addressOutput.setCountry(this.translate.instant(`VALUE.COUNTRY_${this.form.controls.country.value}`));
    }

    return this.addressOutput.output('array').map((part) => part.join(' ')).join(', ');
  }

  private validateInput(input: string, pattern: RegExp): boolean {
    return pattern.test(input);
  }

  private formatCoordinateDisplay(coordinate: any): string {
    return ((coordinate) ? coordinate.toString().replace('.', ',') : '');
  }

  private formatCoordinateSave(coordinate: string): string {
    return ((coordinate) ? coordinate.replace(',', '.') : '');
  }
}
